/**
 * ErrorBoundary.js
 *
 * v1 had no error boundary anywhere. Because several of its render paths threw
 * on ordinary data (`categories.find(...).name` on a missing category,
 * `item.partyName.toLowerCase()` on a document that stores partyDetails.name,
 * `dtx.taxAmount.toFixed(2)` on a line without a tax amount), a single legacy
 * record blanked the entire application to a white screen with no recovery.
 */

import { Component } from 'react';
import { WarningIcon } from './ui/icons';

export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    // eslint-disable-next-line no-console
    console.error('[boundary] a component tree crashed', error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  handleReset = () => {
    this.setState({ error: null, errorInfo: null });
    this.props.onReset?.();
  };

  render() {
    const { error, errorInfo } = this.state;
    const { children, fallback, title = 'Something Went Wrong' } = this.props;

    if (!error) return children;
    if (fallback) return fallback(error, this.handleReset);

    return (
      <div className="empty-state" role="alert">
        <div
          className="empty-state-icon"
          style={{ color: 'var(--danger-600)', background: 'var(--danger-50)' }}
        >
          <WarningIcon size={20} aria-hidden="true" />
        </div>
        <div>
          <div className="empty-state-title">{title}</div>
          <p className="empty-state-description mt-2">
            This part of the app hit an unexpected error. Your data has not been changed.
          </p>
        </div>
        <div className="row gap-2">
          <button type="button" className="btn btn-primary" onClick={this.handleReset}>
            Try Again
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => window.location.reload()}
          >
            Reload the Page
          </button>
        </div>

        {process.env.NODE_ENV !== 'production' && (
          <details className="mt-4" style={{ maxWidth: 640, textAlign: 'left' }}>
            <summary className="text-xs text-tertiary" style={{ cursor: 'pointer' }}>
              Developer Details
            </summary>
            <pre
              className="text-xs mt-2"
              style={{
                overflow: 'auto',
                padding: 'var(--space-3)',
                background: 'var(--bg-subtle)',
                borderRadius: 'var(--radius-md)',
              }}
            >
              {error.toString()}
              {errorInfo?.componentStack}
            </pre>
          </details>
        )}
      </div>
    );
  }
}

export default ErrorBoundary;
